/**
 * InterChallengesView — Liste + gestion des challenges inter-entreprises (admin)
 * La création se fait via InterChallengeWizard (tunnel 6 étapes)
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Plus, Loader2, AlertCircle, Globe, Users, Calendar, Play, Pause, Square, Trash2, Pencil, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { challengesApi } from "../lib/api";
import { InterChallengeWizard } from "./InterChallengeWizard";
import { ChallengeBriefPage } from "./ChallengeBriefPage";
import { pickChallengeTitle, pickChallengeFictiveCompanyName } from "../lib/challenge-i18n";
import { challengePermissions, resumeBlockedByEndDate } from "../lib/challenge-permissions";
import { isChallengeStarted } from "../lib/challenge-status";
import { challengeErrorMessage } from "../lib/challenge-participants";

interface InterChallenge {
  id: string;
  title: string;
  description: string | null;
  scope: string;
  status: string;
  fictiveCompanyName: string | null;
  startDate: string | null;
  endDate: string | null;
  participantsCount: number;
  createdBy?: string | null;
  /** Calculé par le serveur : un challenge qui a vécu ne se supprime jamais. */
  hasLived?: boolean;
  // Variantes i18n (présentes dans la réponse full-row de getChallenges)
  titleFr?: string | null;
  titleEn?: string | null;
  fictiveCompanyNameFr?: string | null;
  fictiveCompanyNameEn?: string | null;
}

export function InterChallengesView({ viewerProfile }: { viewerProfile?: string } = {}) {
  const { t, i18n } = useTranslation();
  // Écran réservé aux internes (Admin) : l'identifiant du lecteur ne sert qu'à la règle du MNG
  // auteur, qui ne s'applique pas ici. Sans profil connu, aucun geste n'est proposé.
  const viewer = { id: "", profileType: viewerProfile ?? null };
  // Challenge lancé (en cours ou en pause) : seules la description et la date de fin se modifient
  // (règle du serveur) — petit formulaire au lieu de l'assistant complet.
  const [startedEdit, setStartedEdit] = useState<{ id: string; description: string; endDate: string } | null>(null);
  const [startedEditError, setStartedEditError] = useState<string | null>(null);
  const [startedEditSaving, setStartedEditSaving] = useState(false);
  const isFr = i18n.language === "fr";

  const [challenges, setChallenges] = useState<InterChallenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingChallengeId, setEditingChallengeId] = useState<string | null>(null);
  const [briefChallengeId, setBriefChallengeId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: "activate" | "pause" | "resume" | "end" | "delete"; challengeId: string; title: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadChallenges = async () => {
    setLoading(true);
    setError("");
    try {
      const all = await challengesApi.getChallenges();
      const inter = (all || []).filter((c: any) => c.scope && c.scope !== "intra");
      setChallenges(inter);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChallenges(); }, []);

  const scopeLabel = (scope: string) => {
    if (scope === "inter_public") return isFr ? "Tout public" : "Public";
    if (scope === "inter_cross_clients") return isFr ? "Clients sélectionnés" : "Selected clients";
    return scope;
  };

  const statusLabel = (status: string) => {
    if (status === "draft") return isFr ? "Brouillon" : "Draft";
    if (status === "active") return isFr ? "Actif" : "Active";
    if (status === "ended") return isFr ? "Terminé" : "Ended";
    if (status === "paused") return t("challenge.statusPaused");
    return status;
  };

  const statusColor = (status: string) => {
    if (status === "draft") return "#607D8B";
    if (status === "active") return "#7CBF94";
    if (status === "ended") return "#F49390";
    if (status === "paused") return "#926B00";
    return "#384872";
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5" style={{ color: "#F6C847" }} />
          <h2 className="text-lg font-bold" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
            {isFr ? "Challenges inter-entreprises" : "Inter-company challenges"}
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-700">{challenges.length}</span>
        </div>
        <Button onClick={() => { setEditingChallengeId(null); setWizardOpen(true); }} size="sm" style={{ backgroundColor: "#F6C847", color: "#926B00" }} className="hover:opacity-90">
          <Plus className="h-4 w-4 mr-1" />
          {isFr ? "Nouveau challenge inter" : "New inter challenge"}
        </Button>
      </div>

      {/* Loading / Error */}
      {loading && <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
      {error && <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-3 text-sm"><AlertCircle className="h-4 w-4 shrink-0" /><p>{error}</p></div>}

      {/* Empty state */}
      {!loading && challenges.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>{isFr ? "Aucun challenge inter-entreprises créé." : "No inter-company challenges created."}</p>
          <p className="text-xs mt-1">{isFr ? "Cliquez sur \"Nouveau challenge inter\" pour commencer." : "Click \"New inter challenge\" to get started."}</p>
        </div>
      )}

      {/* Challenge list */}
      {!loading && challenges.length > 0 && (
        <div className="space-y-3">
          {challenges.map((ch) => (
            <Card key={ch.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold" style={{ color: "#384872" }}>{pickChallengeTitle(ch, i18n.language)}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: statusColor(ch.status) + "20", color: statusColor(ch.status) }}>
                        {statusLabel(ch.status)}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F6C847]/20 text-[#926B00] font-medium">
                        {scopeLabel(ch.scope)}
                      </span>
                    </div>
                    {pickChallengeFictiveCompanyName(ch, i18n.language) && (
                      <p className="text-xs text-muted-foreground mt-1">🏢 {pickChallengeFictiveCompanyName(ch, i18n.language)}</p>
                    )}
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      {ch.startDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(ch.startDate).toLocaleDateString(isFr ? "fr-FR" : "en-GB")}
                          {ch.endDate && ` → ${new Date(ch.endDate).toLocaleDateString(isFr ? "fr-FR" : "en-GB")}`}
                        </span>
                      )}
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{ch.participantsCount} {isFr ? "inscrits" : "enrolled"}</span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-8 px-2" title={isFr ? "Voir le brief" : "View brief"}
                      onClick={() => setBriefChallengeId(ch.id)}>
                      <BookOpen className="h-3 w-3 text-[#384872]" />
                    </Button>
                    {/* Chaque geste = règle partagée avec le serveur (`challengePermissions`), comme l'intra :
                        en pause, le challenge se modifie (description, date de fin) mais ne se supprime pas ;
                        brouillon jamais lancé seulement pour la suppression (décision du 30/09/2026). */}
                    {(() => {
                      const perms = challengePermissions(viewer, ch);
                      const title = pickChallengeTitle(ch, i18n.language);
                      return (
                        <>
                          {perms.edit && (
                            <Button size="sm" variant="outline" className="h-8 px-2" title={isFr ? "Modifier" : "Edit"}
                              onClick={() => {
                                if (isChallengeStarted(ch.status)) {
                                  setStartedEdit({ id: ch.id, description: ch.description ?? "", endDate: (ch.endDate ?? "").slice(0, 10) });
                                  setStartedEditError(null);
                                } else { setEditingChallengeId(ch.id); setWizardOpen(true); }
                              }}>
                              <Pencil className="h-3 w-3 text-gray-600" />
                            </Button>
                          )}
                          {perms.activate && (
                            <Button size="sm" variant="outline" className="h-8 px-2" title={isFr ? "Activer" : "Activate"}
                              onClick={() => setConfirmAction({ type: "activate", challengeId: ch.id, title })}>
                              <Play className="h-3 w-3 text-green-600" />
                            </Button>
                          )}
                          {perms.delete && (
                            <Button size="sm" variant="outline" className="h-8 px-2" title={isFr ? "Supprimer" : "Delete"}
                              onClick={() => setConfirmAction({ type: "delete", challengeId: ch.id, title })}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          )}
                          {/* Annonce : ADM seul, challenge actif (règle du serveur `handleAnnounceChallenge`). */}
                          {ch.status === "active" && viewerProfile === "ADM" && (
                            <Button size="sm" variant="outline" className="h-8 px-2" title={isFr ? "Annoncer par email" : "Announce by email"}
                              onClick={async () => {
                                if (!confirm(isFr ? "Envoyer l'email d'annonce à tous les users éligibles ?" : "Send announcement email to all eligible users?")) return;
                                try {
                                  const res = await fetch(`/api/admin/challenges/${ch.id}/announce`, { method: "POST", credentials: "include" });
                                  const json = await res.json();
                                  if (!res.ok) throw new Error(json.error);
                                  alert(isFr ? `✅ ${json.sent} email(s) envoyé(s) sur ${json.total} éligible(s)` : `✅ ${json.sent} email(s) sent to ${json.total} eligible`);
                                } catch (err: any) { alert(err.message); }
                              }}>
                              <span className="text-xs">📢</span>
                            </Button>
                          )}
                          {perms.pause && (
                            <Button size="sm" variant="outline" className="h-8 px-2" title={isFr ? "Mettre en pause" : "Pause"}
                              onClick={() => setConfirmAction({ type: "pause", challengeId: ch.id, title })}>
                              <Pause className="h-3 w-3 text-orange-500" />
                            </Button>
                          )}
                          {perms.resume && (
                            <Button size="sm" variant="outline" className="h-8 px-2" title={t("challenge.resume")}
                              onClick={() => setConfirmAction({ type: "resume", challengeId: ch.id, title })}>
                              <Play className="h-3 w-3 text-green-600" />
                            </Button>
                          )}
                          {/* Date de fin passée pendant la pause : on prolonge d'abord (« Modifier »), puis on reprend. */}
                          {resumeBlockedByEndDate(viewer, ch) && (
                            <span className="self-center text-[11px] max-w-[14rem]" style={{ color: "#926B00" }}>{t("challenge.resumeNeedsNewEndDate")}</span>
                          )}
                          {perms.end && (
                            <Button size="sm" variant="outline" className="h-8 px-2" title={t("challenge.end")}
                              onClick={() => setConfirmAction({ type: "end", challengeId: ch.id, title })}>
                              <Square className="h-3 w-3 text-red-500" />
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Brief view */}
      {briefChallengeId && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          <ChallengeBriefPage
            challengeId={briefChallengeId}
            onBack={() => setBriefChallengeId(null)}
          />
        </div>
      )}

      {/* Confirm action dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(v) => { if (!v) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === "activate" ? (isFr ? "Activer le challenge" : "Activate challenge") :
               confirmAction?.type === "pause" ? (isFr ? "Mettre en pause" : "Pause challenge") :
               confirmAction?.type === "resume" ? t("challenge.confirmResume") :
               confirmAction?.type === "end" ? (isFr ? "Terminer le challenge" : "End challenge") :
               (isFr ? "Supprimer le challenge" : "Delete challenge")}
            </DialogTitle>
            <DialogDescription>
              {isFr ? `Êtes-vous sûr pour "${confirmAction?.title}" ?` : `Are you sure about "${confirmAction?.title}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={actionLoading}>
              {isFr ? "Annuler" : "Cancel"}
            </Button>
            <Button
              variant={confirmAction?.type === "delete" || confirmAction?.type === "end" ? "destructive" : "default"}
              disabled={actionLoading}
              onClick={async () => {
                if (!confirmAction) return;
                setActionLoading(true);
                try {
                  if (confirmAction.type === "activate") await challengesApi.activateChallenge(confirmAction.challengeId);
                  else if (confirmAction.type === "pause") await challengesApi.pauseChallenge(confirmAction.challengeId);
                  else if (confirmAction.type === "resume") await challengesApi.resumeChallenge(confirmAction.challengeId);
                  else if (confirmAction.type === "end") await challengesApi.endChallenge(confirmAction.challengeId);
                  else if (confirmAction.type === "delete") await challengesApi.deleteChallenge(confirmAction.challengeId);
                  loadChallenges();
                  setConfirmAction(null);
                } catch (err: any) {
                  alert(err.message);
                } finally {
                  setActionLoading(false);
                }
              }}
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isFr ? "Confirmer" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modification d'un challenge lancé (en cours ou en pause) */}
      <Dialog open={!!startedEdit} onOpenChange={(v) => { if (!v) setStartedEdit(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("challenge.edit")}</DialogTitle>
            <DialogDescription>{t("challenge.startedEditHint")}</DialogDescription>
          </DialogHeader>
          {startedEdit && (
            <div className="space-y-3">
              <label className="block text-xs font-semibold" style={{ color: "#5A6076" }}>
                {t("challenge.description")}
                <textarea className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" rows={4} value={startedEdit.description}
                  onChange={(e) => setStartedEdit({ ...startedEdit, description: e.target.value })} />
              </label>
              <label className="block text-xs font-semibold" style={{ color: "#5A6076" }}>
                {t("challenge.endDate")}
                <input type="date" className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={startedEdit.endDate}
                  onChange={(e) => setStartedEdit({ ...startedEdit, endDate: e.target.value })} />
              </label>
              {startedEditError && <p className="text-sm" style={{ color: "#C0423F" }} role="alert">{startedEditError}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartedEdit(null)} disabled={startedEditSaving}>{t("common.cancel")}</Button>
            <Button disabled={startedEditSaving} onClick={async () => {
              if (!startedEdit) return;
              setStartedEditSaving(true); setStartedEditError(null);
              try {
                await challengesApi.updateChallenge(startedEdit.id, { description: startedEdit.description, endDate: startedEdit.endDate || null });
                setStartedEdit(null);
                loadChallenges();
              } catch (e) {
                setStartedEditError(challengeErrorMessage(e, t));
              } finally {
                setStartedEditSaving(false);
              }
            }}>
              {startedEditSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wizard */}
      <InterChallengeWizard
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); setEditingChallengeId(null); }}
        onCreated={() => { setWizardOpen(false); setEditingChallengeId(null); loadChallenges(); }}
        editingChallengeId={editingChallengeId}
      />
    </div>
  );
}
